import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  Sparkles, 
  BookOpen, 
  Calculator,
  Atom,
  Globe,
  Palette,
  Code,
  Music,
  Heart,
  Check,
  ArrowRight,
  PartyPopper,
  Shield,
  Mic,
  Brain
} from 'lucide-react';
import axios from 'axios';

const SUBJECT_OPTIONS = [
  { id: 'math', name: 'Mathematics', icon: Calculator, color: '#00F0FF' },
  { id: 'science', name: 'Science', icon: Atom, color: '#CCFF00' },
  { id: 'history', name: 'History', icon: Globe, color: '#FF6B6B' },
  { id: 'literature', name: 'Literature', icon: BookOpen, color: '#A78BFA' },
  { id: 'arts', name: 'Arts', icon: Palette, color: '#FF0099' },
  { id: 'coding', name: 'Coding', icon: Code, color: '#00FF88' },
  { id: 'music', name: 'Music', icon: Music, color: '#FFB800' },
  { id: 'health', name: 'Health', icon: Heart, color: '#FF4D6D' },
];

const OnboardingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState(1);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  
  // Consent state
  const [consent, setConsent] = useState({
    audio_recording: true,
    ai_summarization: true,
    data_analytics: false,
    marketing_emails: false,
  });
  
  const user = location.state?.user;
  const API = process.env.REACT_APP_BACKEND_URL;
  // Onboarding compressed (Feb 2026): 4 steps → 2.
  //   Step 1 — Pick 1–3 subjects (formerly step 2)
  //   Step 2 — Consent (formerly step 3)
  // Handle prompt and "Ready" confetti screen were removed; handle is
  // auto-suggested from user.name/email and editable later in Profile.
  const totalSteps = 2;

  // Auto-derive a handle so we never block the user on a "claim your name"
  // step. They can rename it later in Profile. Generated lazily inside
  // handleComplete so it isn't recomputed (with Math.random) every render.
  const deriveUsername = () => {
    if (user?.name) {
      const fromName = user.name.toLowerCase().split(' ')[0].replace(/[^a-z0-9_]/g, '').slice(0, 20);
      if (fromName) return fromName;
    }
    if (user?.email) {
      const fromEmail = user.email.toLowerCase().split('@')[0].replace(/[^a-z0-9_]/g, '').slice(0, 20);
      if (fromEmail) return fromEmail;
    }
    return `student${Math.floor(Math.random() * 9000 + 1000)}`;
  };

  const toggleSubject = (subjectId) => {
    setSelectedSubjects(prev => 
      prev.includes(subjectId) 
        ? prev.filter(id => id !== subjectId)
        : [...prev, subjectId]
    );
  };

  const handleNext = async () => {
    if (step === 1 && selectedSubjects.length === 0) {
      toast.error('Pick at least one subject');
      return;
    }
    // Step 2 is the final consent screen → save consent + complete + go to /app
    if (step === 2) {
      try {
        await axios.put(`${API}/api/privacy/consent`, consent);
      } catch (e) {
        console.error('Failed to save consent:', e);
      }
      handleComplete();
      return;
    }
    setStep(prev => prev + 1);
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      // Save onboarding data (auto-derived handle — user can rename in Profile)
      const response = await fetch(`${API}/api/auth/complete-onboarding`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: deriveUsername(),
          subjects: selectedSubjects,
          user_id: user?.user_id
        })
      });

      if (!response.ok) throw new Error('Failed to save preferences');

      // One-shot confetti, then go straight to the app. Removed the dedicated
      // "Ready!" screen — it added a step without giving the user value.
      setShowConfetti(true);
      setTimeout(() => {
        navigate('/app', { replace: true });
      }, 1400);
    } catch (error) {
      console.error('Onboarding error:', error);
      toast.error('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  const stepVariants = {
    enter: { opacity: 0, x: 50 },
    center: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -50 }
  };

  // Pre-compute confetti particle styles once so render() stays pure.
  // useState with a lazy initializer runs the impure Math.random() exactly
  // once at mount and never on re-renders — satisfies react-hooks/purity
  // without sacrificing the visual randomness.
  const [confettiParticles] = useState(() =>
    Array.from({ length: 50 }, (_, i) => ({
      id: i,
      left: `${Math.random() * 100}%`,
      bg: ['#CCFF00', '#00F0FF', '#FF0099', '#8B5CF6', '#FFB800'][Math.floor(Math.random() * 5)],
      radius: Math.random() > 0.5 ? '50%' : '0',
      delay: `${Math.random() * 2}s`,
      duration: `${2 + Math.random() * 2}s`,
    }))
  );

  return (
    <div className="min-h-screen cyber-bg relative overflow-hidden flex items-center justify-center p-4">
      {/* Confetti Animation */}
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none z-50">
          {confettiParticles.map((p) => (
            <div
              key={`item-${p.id}`}
              className="absolute confetti"
              style={{
                left: p.left,
                top: '-20px',
                width: '10px',
                height: '10px',
                backgroundColor: p.bg,
                borderRadius: p.radius,
                animationDelay: p.delay,
                animationDuration: p.duration,
              }}
            />
          ))}
        </div>
      )}

      {/* Background Glow */}
      <div 
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full opacity-20"
        style={{
          background: 'radial-gradient(circle, rgba(139, 92, 246, 0.2) 0%, transparent 70%)'
        }}
      />

      {/* Progress Bar — 2 steps now (subjects, consent) */}
      <div className="absolute top-8 left-1/2 -translate-x-1/2 w-full max-w-xs px-4">
        <div className="flex items-center justify-between mb-2">
          {[1, 2].map((s) => (
            <div
              key={s}
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${
                s < step 
                  ? 'bg-[#CCFF00] text-black' 
                  : s === step 
                    ? 'bg-purple-500 text-white' 
                    : 'bg-white/10 text-white/40'
              }`}
            >
              {s < step ? <Check className="w-4 h-4" /> : s}
            </div>
          ))}
        </div>
        <div className="h-1 bg-white/10 rounded-full overflow-hidden">
          <motion.div 
            className="h-full bg-gradient-to-r from-purple-500 to-[#CCFF00] progress-glow"
            initial={{ width: '0%' }}
            animate={{ width: `${((step - 1) / (totalSteps - 1)) * 100}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>
      </div>

      {/* Main Content */}
      <div className="w-full max-w-md relative z-10">
        <AnimatePresence mode="wait">
          {/* Step 1: Pick Subjects */}
          {step === 1 && (
            <motion.div
              key="step1"
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3 }}
              className="glass-card rounded-3xl p-8"
            >
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16  bg-gradient-to-br from-purple-500 to-pink-500 mb-4">
                  <BookOpen className="w-8 h-8 text-white" />
                </div>
                <h2 className="font-outfit text-2xl md:text-3xl font-bold text-white mb-2">
                  Pick Your Focus
                </h2>
                <p className="text-white/60 text-sm">
                  Select subjects you want to master (pick multiple!)
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-6">
                {SUBJECT_OPTIONS.map((subject) => {
                  const Icon = subject.icon;
                  const isSelected = selectedSubjects.includes(subject.id);
                  return (
                    <motion.button
                      key={subject.id}
                      onClick={() => toggleSubject(subject.id)}
                      className={`relative p-4  border-2 transition-all duration-200 text-left ${
                        isSelected 
                          ? 'border-white/30 bg-white/10' 
                          : 'border-white/10 bg-white/5 hover:border-white/20'
                      }`}
                      whileTap={{ scale: 0.95 }}
                      data-testid={`subject-${subject.id}`}
                    >
                      {isSelected && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="absolute top-2 right-2 w-5 h-5 rounded-full bg-[#CCFF00] flex items-center justify-center"
                        >
                          <Check className="w-3 h-3 text-black" />
                        </motion.div>
                      )}
                      <Icon 
                        className="w-6 h-6 mb-2" 
                        style={{ color: subject.color }}
                      />
                      <span className="text-white text-sm font-medium">
                        {subject.name}
                      </span>
                    </motion.button>
                  );
                })}
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={handleNext}
                  disabled={selectedSubjects.length === 0}
                  className="flex-1 h-12 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 hover:opacity-90 text-white font-bold glow-button"
                  data-testid="onboarding-next-step1"
                >
                  Continue
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* Step 2: Privacy & Consent (final) */}
          {step === 2 && (
            <motion.div
              key="step2"
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3 }}
              className="glass-card rounded-3xl p-8"
              data-testid="onboarding-step-consent"
            >
              <div className="text-center mb-5">
                <div className="inline-flex items-center justify-center w-16 h-16  bg-[#0A0A0A] mb-4">
                  <Shield className="w-8 h-8 text-white" />
                </div>
                <h2 className="font-outfit text-2xl md:text-3xl font-bold text-white mb-2">
                  Your Data, Your Rules.
                </h2>
                <p className="text-white/60 text-sm">
                  We need your consent to power your study experience. Change anytime in Settings.
                </p>
              </div>

              <div className="space-y-2 mb-5">
                {[
                  { key: 'audio_recording', label: 'Audio Recording & Transcription', desc: 'Allow cloud processing of lecture recordings', icon: Mic, color: '#FF6B6B', required: true },
                  { key: 'ai_summarization', label: 'AI Study Tools', desc: 'Generate flashcards, summaries & study plans', icon: Brain, color: '#A78BFA', required: true },
                  { key: 'data_analytics', label: 'Usage Analytics', desc: 'Help us improve with anonymized data', icon: Globe, color: '#00F0FF', required: false },
                  { key: 'marketing_emails', label: 'Product Updates', desc: 'Tips, features & study hacks by email', icon: Sparkles, color: '#FFB800', required: false },
                ].map((item) => {
                  const Icon = item.icon;
                  const isOn = consent[item.key];
                  return (
                    <motion.button
                      key={item.key}
                      type="button"
                      onClick={() => {
                        if (item.required) return;
                        setConsent(prev => ({ ...prev, [item.key]: !prev[item.key] }));
                      }}
                      className={`w-full flex items-center gap-3 p-3.5  border-2 transition-all text-left ${
                        isOn
                          ? 'border-white/20 bg-white/10'
                          : 'border-white/5 bg-white/5 hover:border-white/15'
                      } ${item.required ? 'cursor-default' : 'cursor-pointer'}`}
                      whileTap={item.required ? {} : { scale: 0.98 }}
                      data-testid={`onboarding-consent-${item.key}`}
                    >
                      <div className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 transition-colors"
                        style={{ backgroundColor: isOn ? item.color : 'rgba(255,255,255,0.1)' }}
                      >
                        {isOn && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <Icon className="w-4 h-4 flex-shrink-0" style={{ color: item.color }} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white leading-tight">
                          {item.label}
                          {item.required && <span className="text-[10px] ml-1.5 font-semibold" style={{ color: item.color }}>Required</span>}
                        </p>
                        <p className="text-[11px] text-white/40 leading-tight mt-0.5">{item.desc}</p>
                      </div>
                    </motion.button>
                  );
                })}
              </div>

              <p className="text-[10px] text-white/30 text-center mb-4">
                Ghana DPA 2012 & GDPR compliant. See our <a href="/legal#aup" target="_blank" className="text-[#CCFF00] underline">Acceptable Use Policy</a> and <a href="/legal#privacy" target="_blank" className="text-[#CCFF00] underline">Privacy Policy</a>.
              </p>

              <div className="flex gap-3">
                <Button
                  onClick={() => setStep(1)}
                  variant="outline"
                  className="flex-1 h-12 rounded-full border-white/20 text-white/70 hover:text-white hover:bg-white/5"
                  data-testid="onboarding-back-step2"
                >
                  Back
                </Button>
                <Button
                  onClick={handleNext}
                  disabled={loading || showConfetti}
                  className="flex-1 h-12 rounded-full bg-[#0A0A0A] hover:opacity-90 text-white font-bold glow-button"
                  data-testid="onboarding-consent-agree-btn"
                >
                  {loading || showConfetti ? (
                    <>
                      <PartyPopper className="w-4 h-4 mr-2 animate-bounce" />
                      Let&apos;s Go!
                    </>
                  ) : (
                    <>
                      I Agree &amp; Continue
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default OnboardingPage;
